// Ensure React is loaded first before any other imports
import 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initPersistedStorage } from './utils/local-storage.ts';
import { App } from './components';
import * as Sentry from '@sentry/react';
import { isSentryBlocked } from './utils/adblocker-detection';

// Initialize Sentry conditionally based on adblocker detection
const initializeSentry = async () => {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!sentryDsn) {
    return; // No DSN provided
  }
  
  // Check if Sentry endpoint is blocked
  const isBlocked = await isSentryBlocked();
  
  if (isBlocked) {
    // Skip Sentry initialization if endpoint is blocked
    console.log('Sentry endpoint blocked - skipping Sentry initialization');
    return;
  }

  // Initialize Sentry only if no adblocker is present
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Standard settings for users without adblockers
    tracesSampleRate: 1.0, // Full performance monitoring
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // All error sessions
  });
};

// Initialize Sentry asynchronously
initializeSentry();

void initPersistedStorage();

(() => {
  if (!(BigInt.prototype as $TSFixMe).toJSON) {
    Object.defineProperty(BigInt.prototype, 'toJSON', {
      value: function toJSON(this: bigint) {
        return this.toString();
      },
      configurable: true,
      writable: true,
    });
  }
})();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Something went wrong:</h2>
        <pre>{error instanceof Error ? error.message : String(error)}</pre>
        <button onClick={resetError}>Try again</button>
      </div>
    )}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
